import { SliderProductItem } from 'components/Blocks/Product/SliderProductItem';
import { desktopFirstSizes } from 'components/Theme/mediaQueries';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import useEmblaCarousel, { EmblaOptionsType } from 'embla-carousel-react';
import { isB2bDomain } from 'helpers/isDomainLocale';
import { useGtmProductListView } from 'hooks/gtm/useGtmProductListView';
import { useGetVisibleSliderIndexes } from 'hooks/slider/useGetVisibleSliderIndexes';
import { useLatest } from 'hooks/ui/useLatest';
import { useMediaMin } from 'hooks/ui/useMediaMin';
import { useWishlist } from 'hooks/wishlist/useWishlist';
import useTranslation from 'next-translate/useTranslation';
import { ArrowIcon } from 'public/svg/IconsSvg';
import { useCallback, useEffect, useRef, useState } from 'react';
import { GtmListNameType } from 'types/gtm';
import { SliderProductItemType } from 'types/product';
import twMerge from 'utils/twMerge';

const INITIAL_SLIDER_SETTINGS: EmblaOptionsType = {
    containScroll: 'trimSnaps',
    dragFree: false,
    watchDrag: true,
    duration: 20,
    startIndex: 0,
};

function getSliderSettings(width: number, showNavigation: boolean): EmblaOptionsType {
    if (width <= desktopFirstSizes.mobile) {
        return {
            ...INITIAL_SLIDER_SETTINGS,
            align: 'center',
            watchDrag: showNavigation,
        };
    }

    return {
        ...INITIAL_SLIDER_SETTINGS,
        align: 'start',
        watchDrag: showNavigation,
        loop: false,
        skipSnaps: false,
    };
}

type ProductsSliderProps = {
    className?: string;
    idSuffix?: string;
    gtmListName: GtmListNameType;
    noMarginBottom?: boolean;
    shouldRedirectOnClick?: boolean;
    products: SliderProductItemType[];
    onProductItemClick?: (product: SliderProductItemType) => void;
    isConfigurator?: boolean;
    maxVisibleItems?: number;
    onAddToCart?: (productUuid: string) => void;
};

const productSliderArrowTwClass =
    'absolute top-1/2 z-[481] -mt-1 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center border-none bg-transparent text-white outline-none transition-colors duration-200 ease-defaultTransition';

const ProductsSlider: FC<ProductsSliderProps> = ({
    className,
    idSuffix,
    gtmListName,
    noMarginBottom,
    products,
    onProductItemClick,
    shouldRedirectOnClick = true,
    isConfigurator,
    maxVisibleItems,
    onAddToCart,
}) => {
    const domainConfig = useDomainConfig();
    useGtmProductListView(products, gtmListName);
    const { t } = useTranslation();
    const [prevButtonEnabled, setPrevButtonEnabled] = useState(false);
    const [nextButtonEnabled, setNextButtonEnabled] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [emblaApiReady, setEmblaApiReady] = useState(false);

    const [emblaRef, emblaApi] = useEmblaCarousel(INITIAL_SLIDER_SETTINGS);
    const latestEmblaApi = useLatest(emblaApi);

    const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
    const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

    const containerRef = useRef<HTMLDivElement>(null);

    /* watch if embla api is ready and bind its select event */
    useEffect(() => {
        if (!emblaApi) {
            return void null;
        }
        setEmblaApiReady(true);

        const onSelectHandler = () => {
            setPrevButtonEnabled(emblaApi.canScrollPrev());
            setNextButtonEnabled(emblaApi.canScrollNext());
            setSelectedIndex(emblaApi.selectedScrollSnap());
        };
        onSelectHandler();
        emblaApi.on('select', onSelectHandler);

        return () => {
            emblaApi.off('select', onSelectHandler);
        };
    }, [emblaApi]);

    /* if embla api is ready listen for window width or products changes and re-initialize */
    useEffect(() => {
        const _emblaApi = latestEmblaApi.current;
        if (!emblaApiReady || !_emblaApi) {
            return;
        }

        _emblaApi.reInit(getSliderSettings(window.innerWidth, _emblaApi.canScrollPrev() || _emblaApi.canScrollNext()));
        setPrevButtonEnabled(_emblaApi.canScrollPrev());
        setNextButtonEnabled(_emblaApi.canScrollNext());
        setSelectedIndex(_emblaApi.selectedScrollSnap());
    }, [emblaApiReady, latestEmblaApi, products]);

    const isMobile = !useMediaMin('lg');
    const isTablet = !useMediaMin('vl');
    const isTabletLarge = !useMediaMin('xl');

    const getVisibleSliderIndexes = useCallback(() => {
        let length;

        switch (true) {
            case isMobile:
                length = 2;
                break;
            case isTablet:
                length = 3;
                break;
            case isTabletLarge:
                length = 4;
                break;
            default:
                length = 5;
                break;
        }

        if (maxVisibleItems) {
            length = Math.min(maxVisibleItems, length);
        }

        return Array.from({ length }, (_, index) => index + selectedIndex);
    }, [isMobile, isTablet, isTabletLarge, maxVisibleItems, selectedIndex]);

    const visibleSliderIndexes = useGetVisibleSliderIndexes(emblaApi, getVisibleSliderIndexes());
    const { toggleProductInWishlist, isProductInWishlist } = useWishlist();

    return (
        <div className="relative">
            {(prevButtonEnabled || nextButtonEnabled) && (
                <>
                    <button
                        aria-label={t('Scroll to previous product')}
                        tabIndex={0}
                        className={twMerge(
                            productSliderArrowTwClass,
                            !prevButtonEnabled && 'pointer-events-none hidden md:flex',
                            'left-0 rotate-90 md:-top-10 md:left-[unset] md:right-12 md:translate-y-0',
                        )}
                        onClick={scrollPrev}
                    >
                        <span
                            className={twMerge(
                                'flex h-10 w-10 items-center justify-center rounded-full text-currentColor transition-colors duration-200 ease-defaultTransition active:scale-[0.97]',
                                prevButtonEnabled ? 'bg-black hover:bg-green' : 'bg-grayLighter',
                            )}
                        >
                            <ArrowIcon className="h-4 w-4 text-currentColor" />
                        </span>
                    </button>
                    <button
                        aria-label={t('Scroll to next product')}
                        tabIndex={0}
                        className={twMerge(
                            productSliderArrowTwClass,
                            !nextButtonEnabled && 'pointer-events-none hidden md:flex',
                            'right-0 -rotate-90 md:-top-10 md:right-0 md:translate-y-0',
                        )}
                        onClick={scrollNext}
                    >
                        <span
                            className={twMerge(
                                'flex h-10 w-10 items-center justify-center rounded-full text-currentColor transition-colors duration-200 ease-defaultTransition active:scale-[0.97]',
                                nextButtonEnabled ? 'bg-black hover:bg-green' : 'bg-grayLighter',
                            )}
                        >
                            <ArrowIcon className="h-4 w-4 text-currentColor" />
                        </span>
                    </button>
                </>
            )}
            <div
                ref={emblaRef}
                className={twMerge(
                    'relative min-w-full overflow-hidden py-4',
                    noMarginBottom ? 'mb-0' : 'mb-11 lg:mb-14 lg:mt-6',
                    prevButtonEnabled && 'md:!before:hidden before:!block',
                    nextButtonEnabled && 'md:!after:hidden after:!block',
                    'before:pointer-events-none before:absolute before:bottom-0 before:left-0 before:top-0 before:z-productSlider before:hidden before:w-14 before:content-[""] before:[background:linear-gradient(to_right,_#ffffff_0%,_#ffffff00_100%)]',
                    'after:pointer-events-none after:absolute after:bottom-0 after:right-0 after:top-0 after:z-productSlider after:hidden after:w-14 after:content-[""] after:[background:linear-gradient(to_left,_#ffffff_0%,_#ffffff00_100%)]',
                    className,
                )}
            >
                <div
                    ref={containerRef}
                    className={twMerge(
                        'relative flex w-full',
                        isB2bDomain(domainConfig) ? 'min-h-[568px]' : 'min-h-[448px] lg:min-h-[436px] vl:min-h-[446px]',
                    )}
                >
                    {products.map((productItemData, index) => (
                        <SliderProductItem
                            key={productItemData.uuid}
                            gtmListName={gtmListName}
                            idSuffix={idSuffix}
                            isConfigurator={isConfigurator}
                            isContentVisible={visibleSliderIndexes.includes(index)}
                            isProductInWishlist={isProductInWishlist(productItemData.uuid)}
                            listIndex={index}
                            maxVisibleItems={maxVisibleItems}
                            product={productItemData}
                            selected={index === selectedIndex}
                            shouldRedirectOnClick={shouldRedirectOnClick}
                            toggleProductInWishlist={() => toggleProductInWishlist(productItemData.uuid)}
                            onAddToCart={onAddToCart}
                            onProductItemClick={onProductItemClick}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ProductsSlider;
